import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Users, Search, Trash2 } from "lucide-react";
import { api, errMsg, downloadFile } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Textarea } from "../ui/textarea";
import { DialogDescription, DialogFooter } from "../ui/dialog";
import { dateBR, dateTimeBR } from "../../lib/format";
import { Card } from "../common";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { RequestDetailSheet } from "../RequestDetailSheet";
import { FolhaEmployeeCards } from "./FolhaEmployeeCards";

const LBL = (h) => (h.substituted_by ? `SUBSTITUÍDA (#${h.substituted_by})` : h.status === "concluida" ? "CONCLUÍDA" : ["cancelada", "rejeitada"].includes(h.status) ? "CANCELADA" : h.accepted_at ? "EM PROCESSAMENTO" : "ENVIADA AO DPDIGI");
const dl = (x) => downloadFile(`/documents/${x.id}/download`, x.filename);

function Snap({ rid, onClose }) {
  const [r, setR] = useState(null);
  useEffect(() => { api.get(`/requests/${rid}`).then((x) => setR(x.data)).catch((e) => toast.error(errMsg(e))); }, [rid]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}><DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="folha-history-snapshot">
      <DialogHeader><DialogTitle>{r ? `${r.title} · #${r.protocol}` : "Carregando…"}</DialogTitle></DialogHeader>
      {r && <><p className="text-xs text-slate-500">Dados exatamente como foram enviados em {dateTimeBR(r.created_at)} por {r.requester_name}.</p>
        {Array.isArray(r.data?.snapshot) ? <FolhaEmployeeCards snap={r.data.snapshot} /> : <p className="text-sm text-slate-500">Envio antigo sem lista consolidada.</p>}</>}
    </DialogContent></Dialog>);
}

function HideDialog({ h, onClose, onDone }) {
  const [t, setT] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); try { await api.post(`/folha/history/${h.id}/hide`, { reason: t.trim() }); toast.success("Folha excluída do histórico"); onDone(); onClose(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}><DialogContent data-testid="folha-history-hide-dialog">
      <DialogHeader><DialogTitle>Excluir {h.label} (#{h.protocol}) do histórico?</DialogTitle>
        <DialogDescription>A folha deixa de aparecer aqui. O protocolo, o PDF e os arquivos continuam guardados para auditoria e o DPdigi ainda pode consultá-los.</DialogDescription></DialogHeader>
      <Textarea rows={3} value={t} onChange={(e) => setT(e.target.value)} placeholder="Motivo da exclusão" data-testid="folha-history-hide-reason" />
      <DialogFooter><Button variant="outline" onClick={onClose}>Voltar</Button>
        <Button variant="destructive" disabled={busy || t.trim().length < 3} onClick={go} data-testid="folha-history-hide-confirm">Excluir do histórico</Button></DialogFooter>
    </DialogContent></Dialog>);
}

export function FolhaHistory({ refreshKey }) {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [snap, setSnap] = useState(null);
  const [track, setTrack] = useState(null);
  const [hide, setHide] = useState(null);
  const [rk, setRk] = useState(0);
  useEffect(() => { api.get("/folha/history", { silent: true }).then((x) => setRows(x.data)).catch(() => setRows([])); }, [refreshKey, rk]);
  return (
    <section className="space-y-2" data-testid="folha-history">
      <h2 className="font-display text-lg font-semibold">Histórico de folhas</h2>
      {rows && !rows.length && <Card className="p-6 text-center text-sm text-slate-500">Nenhuma folha enviada ainda.</Card>}
      {(rows || []).map((h) => (
        <Card key={h.id} className="flex flex-wrap items-center gap-3 p-4" testid={`folha-history-${h.id}`}>
          <div className="mr-auto min-w-0">
            <div className="font-semibold">{h.label} <span className="text-xs font-normal text-slate-500">· {h.envio}</span></div>
            <div className="text-xs text-slate-500">#{h.protocol} · enviada {dateTimeBR(h.created_at)}{h.completed_at ? ` · concluída ${dateBR(h.completed_at)}` : ""} · {h.funcionarios ?? "—"} funcionário(s)</div>
            <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-700" data-testid={`folha-history-status-${h.id}`}>{LBL(h)}</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant="outline" onClick={() => setSnap(h.id)} data-testid={`folha-history-employees-${h.id}`}><Users className="mr-1 h-4 w-4" />Ver funcionários</Button>
            {h.report && <Button size="sm" variant="outline" onClick={() => dl(h.report)} data-testid={`folha-history-report-${h.id}`}><Download className="mr-1 h-4 w-4" />Arquivo enviado</Button>}
            {h.returns.length > 0 && <Button size="sm" variant="outline" onClick={() => h.returns.forEach(dl)} data-testid={`folha-history-returns-${h.id}`}><Download className="mr-1 h-4 w-4" />Arquivos devolvidos ({h.returns.length})</Button>}
            <Button size="sm" variant="ghost" onClick={() => setTrack(h.id)} data-testid={`folha-history-track-${h.id}`}><Search className="mr-1 h-4 w-4" />Acompanhar</Button>
            {user?.role === "company_admin" && <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => setHide(h)} data-testid={`folha-history-hide-${h.id}`}><Trash2 className="mr-1 h-4 w-4" />Excluir</Button>}
          </div>
        </Card>))}
      {snap && <Snap rid={snap} onClose={() => setSnap(null)} />}
      {hide && <HideDialog h={hide} onClose={() => setHide(null)} onDone={() => setRk((x) => x + 1)} />}
      <RequestDetailSheet id={track} open={!!track} onOpenChange={(o) => !o && setTrack(null)} onChanged={() => {}} />
    </section>);
}
